import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import TabWrapper from './TabWrapper';
import { Box, Typography, Button, TextField, Grid, Alert, CircularProgress } from '@mui/material';
import { message } from 'antd';
import { ethers } from 'ethers';
import { DevPaymentAbi } from '../abis';
import { sendEmailToDevPartner } from '../../../../../redux/actions/dao.action';
import { inputStyles, buttonStyles } from './css/commonStyles';

const DevPaymentTab = ({ daoOrganizerAddress, chainId, currentDao }) => {
  const USDT_DECIMALS = 6;

  const [milestoneIndex, setMilestoneIndex] = useState('');
  const [amount, setAmount] = useState('');
  const [totalMilestones, setTotalMilestones] = useState('');
  const [totalPayment, setTotalPayment] = useState('');
  const [usdtAmount, setUsdtAmount] = useState('');
  const [katPerUsdt, setKatPerUsdt] = useState('');
  const [currentAddress, setCurrentAddress] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isProjectManager, setIsProjectManager] = useState(false);
  const [isContractor, setIsContractor] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [settingTotalMilestones, setSettingTotalMilestones] = useState(false);
  const [settingTotalPayment, setSettingTotalPayment] = useState(false);
  const [settingMilestone, setSettingMilestone] = useState(false);
  const [onChainTotalMilestonesBN, setOnChainTotalMilestonesBN] = useState(null);
  const [onChainTotalPaymentBN, setOnChainTotalPaymentBN] = useState(null);
  const [manualDaoId, setManualDaoId] = useState('');
  const [manualTxHash, setManualTxHash] = useState('');
  const [manualMilestoneAmount, setManualMilestoneAmount] = useState('');

  const showEmailSuccessToast = () => {
    message.success('Email sent successfully to partner');
  };

  const showEmailErrorToast = (errorMessage) => {
    message.error(errorMessage || 'Failed to send email to partner');
  };

  useEffect(() => {
    if (!currentDao?.devPartnerPaymentAddress) return;
    checkPermissions();
  }, [currentDao]);

  const loadTotals = async (providerOrSigner) => {
    if (!currentDao?.devPartnerPaymentAddress) return;
    const contract = new ethers.Contract(currentDao.devPartnerPaymentAddress, DevPaymentAbi, providerOrSigner);
    const [totalMilestonesBN, totalPaymentBN] = await Promise.all([contract.totalMilestones(), contract.totalPayment()]);

    setOnChainTotalMilestonesBN(totalMilestonesBN);
    setOnChainTotalPaymentBN(totalPaymentBN);

    // Display on-chain values in the inputs by default.
    setTotalMilestones(totalMilestonesBN.toString());
    setTotalPayment(ethers.utils.formatUnits(totalPaymentBN, USDT_DECIMALS));
  };

  const checkPermissions = async () => {
    try {
      if (!window.ethereum) {
        setError('Please install MetaMask');
        return;
      }

      if (!currentDao?.devPartnerPaymentAddress) return;

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const accounts = await provider.send('eth_requestAccounts', []);
      const currentAddress = accounts[0];
      setCurrentAddress(currentAddress);

      const contract = new ethers.Contract(currentDao.devPartnerPaymentAddress, DevPaymentAbi, provider);

      // Check admin role
      const adminAddress = await contract.admin();
      setIsAdmin(adminAddress.toLowerCase() === currentAddress.toLowerCase());

      // Check project manager role
      const projectManagerAddress = await contract.projectManager();
      setIsProjectManager(projectManagerAddress.toLowerCase() === currentAddress.toLowerCase());

      // Check contractor role
      const contractorAddress = await contract.contractor();
      setIsContractor(contractorAddress.toLowerCase() === currentAddress.toLowerCase());

      await loadTotals(provider);
    } catch (error) {
      setError('Error checking permissions');
    }
  };

  const canSetTotalMilestones =
    isProjectManager && onChainTotalMilestonesBN && ethers.BigNumber.from(onChainTotalMilestonesBN).isZero();
  const canSetTotalPayment =
    isProjectManager && onChainTotalPaymentBN && ethers.BigNumber.from(onChainTotalPaymentBN).isZero();
  const permissionLabels = [isAdmin && 'Admin', isProjectManager && 'Project Manager', isContractor && 'Contractor']
    .filter(Boolean)
    .join(' | ');

  const handleSetMilestone = async () => {
    setSuccess('');
    setError('');
    if (!isProjectManager) {
      setError('Only Project Manager can set milestones.');
      return;
    }
    try {
      setSettingMilestone(true);
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(currentDao.devPartnerPaymentAddress, DevPaymentAbi, signer);

      const milestoneIndexBN = ethers.BigNumber.from(milestoneIndex);
      const amountBN = ethers.utils.parseUnits(amount, USDT_DECIMALS);

      const tx = await contract.setMilestone(milestoneIndexBN, amountBN);
      await tx.wait();
      setError('');
      setSuccess('Milestone set successfully');
    } catch (error) {
      setError('Error setting milestone');
    } finally {
      setSettingMilestone(false);
    }
  };

  const handleSetTotalMilestones = async () => {
    setSuccess('');
    setError('');
    if (!isProjectManager) {
      setError('Only Project Manager can set total milestones.');
      return;
    }
    try {
      setSettingTotalMilestones(true);
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(currentDao.devPartnerPaymentAddress, DevPaymentAbi, signer);

      const totalMilestonesBN = ethers.BigNumber.from(totalMilestones);

      const tx = await contract.setTotalMilestones(totalMilestonesBN);
      await tx.wait();
      await loadTotals(provider);
      setError('');
      setSuccess('Total milestones set successfully');
    } catch (error) {
      setError('Error setting total milestones');
    } finally {
      setSettingTotalMilestones(false);
    }
  };

  const handleSetTotalPayment = async () => {
    setSuccess('');
    setError('');
    if (!isProjectManager) {
      setError('Only Project Manager can set total payment.');
      return;
    }
    try {
      setSettingTotalPayment(true);
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(currentDao.devPartnerPaymentAddress, DevPaymentAbi, signer);

      const totalPaymentBN = ethers.utils.parseUnits(totalPayment, USDT_DECIMALS);

      const tx = await contract.setTotalPayment(totalPaymentBN);
      await tx.wait();
      await loadTotals(provider);
      setError('');
      setSuccess('Total payment set successfully');
    } catch (error) {
      setError('Error setting total payment');
    } finally {
      setSettingTotalPayment(false);
    }
  };

  const handleCompleteMilestone = async () => {
    setSuccess('');
    setError('');
    if (!isAdmin) {
      setError('Only Admin can complete milestones.');
      return;
    }

    if (!milestoneIndex || !usdtAmount || !katPerUsdt) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(currentDao.devPartnerPaymentAddress, DevPaymentAbi, signer);

      const milestoneIndexBN = ethers.BigNumber.from(milestoneIndex);
      const usdtAmountBN = ethers.BigNumber.from(usdtAmount);
      const katPerUsdtBN = ethers.BigNumber.from(katPerUsdt);

      const totalMilestones = await contract.totalMilestones();

      if (milestoneIndexBN.gte(totalMilestones)) {
        setError('Milestone index out of range');
        return;
      }

      const milestone = await contract.milestones(milestoneIndexBN);

      if (milestone.completed) {
        setError('Milestone already completed');
        return;
      }

      if (milestone.amount.lte(0)) {
        setError('Milestone amount not set');
        return;
      }

      if (usdtAmountBN.lte(0) || katPerUsdtBN.lte(0)) {
        setError('Exchange rate inputs must be greater than 0');
        return;
      }

      const gasPrice = await provider.getGasPrice();
      const maxGasPrice = ethers.utils.parseUnits('50', 'gwei');
      const finalGasPrice = gasPrice.gt(maxGasPrice) ? maxGasPrice : gasPrice;

      const gasEstimate = await contract.estimateGas.completeMilestone(milestoneIndexBN, usdtAmountBN, katPerUsdtBN);
      const gasLimit = Math.floor(gasEstimate.toNumber() * 1.2);

      const tx = await contract.completeMilestone(milestoneIndexBN, usdtAmountBN, katPerUsdtBN, {
        gasLimit: gasLimit,
        gasPrice: finalGasPrice,
      });

      const receipt = await tx.wait();

      const milestoneAmount = milestone.amount;
      setSuccess(`Milestone ${milestoneIndex} completed successfully`);
      setError(null);

      try {
        await sendEmailToDevPartner(currentDao._id, receipt.transactionHash, milestoneAmount.toString());
        showEmailSuccessToast();
      } catch (error) {
        setLoading(false);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to send email to partner';
        setError(errorMessage);
        showEmailErrorToast(errorMessage);
        return;
      }
      setLoading(false);
    } catch (error) {
      if (error.code === -32603) {
        setError('Transaction failed. Please check your gas limit and try again.');
      } else if (error.message.includes('reverted')) {
        setError(
          'Transaction reverted. Please check if you have the correct permissions and the milestone is in a valid state.',
        );
      } else {
        setError(`Error completing milestone: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmailToDevPartner = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const response = await sendEmailToDevPartner(currentDao._id);
      setSuccess('Email sent successfully to partner');
      showEmailSuccessToast();
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to send email to partner';
      setError(errorMessage);
      showEmailErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <TabWrapper daoOrganizerAddress={daoOrganizerAddress} chainId={chainId}>
      <Box sx={{ p: 4 }}>
        <Typography
          variant="h6"
          sx={{ mb: 3, pb: 2, color: '#fff', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
        >
          Milestone Payment
        </Typography>
        <Typography
          className="saleBetaTitle"
          variant="h6"
          sx={{
            fontSize: '16px',
            fontWeight: 300,
            mb: 3,
            letterSpacing: '0.5px',
            color: '#00EDC3',
          }}
        >
          Milestone Management
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontSize: '14px', fontFamily: 'Questrial, normal', color: '#e7e3e3' }}>
            Current Address: {currentAddress}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '14px', fontFamily: 'Questrial, normal', color: '#e7e3e3' }}>
            Milestone Payment Address: {currentDao?.devPartnerPaymentAddress || '—'}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '14px', fontFamily: 'Questrial, normal', color: '#e7e3e3' }}>
            Permissions:
            {permissionLabels ? ` ${permissionLabels}` : ' None'}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Set Total Milestones */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
                Set Total Milestones
              </Typography>
              <TextField
                fullWidth
                label="Total Milestones"
                value={totalMilestones}
                onChange={(e) => setTotalMilestones(e.target.value)}
                disabled={!canSetTotalMilestones}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                variant="contained"
                onClick={handleSetTotalMilestones}
                sx={buttonStyles}
                disabled={loading || settingTotalMilestones || !canSetTotalMilestones}
                startIcon={settingTotalMilestones && <CircularProgress size={20} color="inherit" />}
              >
                Set Total Milestones
              </Button>
            </Box>
          </Grid>

          {/* Set Total Payment */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
                Set Total Payment
              </Typography>
              <TextField
                fullWidth
                label="Total Payment (USDT)"
                value={totalPayment}
                onChange={(e) => setTotalPayment(e.target.value)}
                disabled={!canSetTotalPayment}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
                placeholder="Enter total payment in USDT"
              />
              <Button
                variant="contained"
                onClick={handleSetTotalPayment}
                sx={buttonStyles}
                disabled={loading || settingTotalPayment || !canSetTotalPayment}
                startIcon={settingTotalPayment && <CircularProgress size={20} color="inherit" />}
              >
                Set Total Payment
              </Button>
            </Box>
          </Grid>

          {/* Set Milestone */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
                Set Milestone
              </Typography>
              <TextField
                fullWidth
                label="Milestone Index"
                value={milestoneIndex}
                onChange={(e) => setMilestoneIndex(e.target.value)}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Amount (USDT)"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
                placeholder="Enter amount in USDT"
              />
              <Button
                variant="contained"
                onClick={handleSetMilestone}
                sx={buttonStyles}
                disabled={loading || settingMilestone}
                startIcon={settingMilestone && <CircularProgress size={20} color="inherit" />}
              >
                Set Milestone
              </Button>
            </Box>
          </Grid>

          {/* Complete Milestone */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
                Complete Milestone
              </Typography>
              <TextField
                fullWidth
                label="Milestone Index"
                value={milestoneIndex}
                onChange={(e) => setMilestoneIndex(e.target.value)}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="USDT Amount (rate numerator)"
                value={usdtAmount}
                onChange={(e) => setUsdtAmount(e.target.value)}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
                placeholder="Usually 1"
              />
              <TextField
                fullWidth
                label="KAT per USDT (rate denominator)"
                value={katPerUsdt}
                onChange={(e) => setKatPerUsdt(e.target.value)}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
                placeholder="Example: 1641"
              />
              <Button
                variant="contained"
                onClick={handleCompleteMilestone}
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} color="inherit" />}
                sx={buttonStyles}
              >
                Complete Milestone
              </Button>
              {loading && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    color: '#00EDC3',
                    fontStyle: 'italic',
                  }}
                >
                  Please do not close this page. Transaction is being processed on the blockchain...
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={12}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
                Send Email to Partner (Manual)
              </Typography>
              <TextField
                fullWidth
                label="Transaction Hash"
                value={manualTxHash}
                onChange={(e) => setManualTxHash(e.target.value)}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
                placeholder="Enter Transaction Hash"
                margin="dense"
              />
              <TextField
                fullWidth
                label="Milestone Amount (USDT)"
                value={manualMilestoneAmount}
                onChange={(e) => setManualMilestoneAmount(e.target.value)}
                sx={inputStyles}
                InputLabelProps={{ shrink: true }}
                placeholder="Enter Milestone Amount in USDT"
                margin="dense"
              />
              <Button
                variant="contained"
                onClick={async () => {
                  const trimmedTxHash = (manualTxHash || '').trim();
                  const trimmedMilestoneAmount = (manualMilestoneAmount || '').trim();

                  if (!trimmedTxHash) {
                    const errorMessage = 'Transaction hash is required';
                    setError(errorMessage);
                    showEmailErrorToast(errorMessage);
                    return;
                  }
                  if (!trimmedMilestoneAmount) {
                    const errorMessage = 'Milestone amount is required';
                    setError(errorMessage);
                    showEmailErrorToast(errorMessage);
                    return;
                  }
                  if (Number.isNaN(Number(trimmedMilestoneAmount))) {
                    const errorMessage = 'Milestone amount must be a valid number';
                    setError(errorMessage);
                    showEmailErrorToast(errorMessage);
                    return;
                  }

                  setLoading(true);
                  setError('');
                  setSuccess('');
                  try {
                    await sendEmailToDevPartner(currentDao._id, trimmedTxHash, trimmedMilestoneAmount);
                    setSuccess('Email sent successfully to partner');
                    showEmailSuccessToast();
                  } catch (error) {
                    const errorMessage = error?.response?.data?.message || error?.message || 'Failed to send email to partner';
                    setError(errorMessage);
                    showEmailErrorToast(errorMessage);
                  } finally {
                    setLoading(false);
                  }
                }}
                sx={buttonStyles}
                disabled={loading}
              >
                Send Email to Partner
              </Button>
            </Box>
            <Button
              variant="contained"
              color="secondary"
              sx={{ ...buttonStyles, mb: 3, background: '#1a1f24', color: '#00EDC3' }}
              onClick={() => window.open('https://app.safe.global/welcome/accounts', '_blank')}
            >
              Go To Safe.global
            </Button>
          </Grid>
        </Grid>
      </Box>
    </TabWrapper>
  );
};

const mapStateToProps = (state) => ({
  currentDao: state.dao?.currentDao || null,
});
const mapDispatchToProps = {
  sendEmailToDevPartner,
};
export default connect(mapStateToProps)(DevPaymentTab);
